import { useContext, splitProps, mergeProps, createEffect, createSignal } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { twJoin, twMerge } from '@troon/tailwind-preset/merge';
import { useFocusable } from '../providers/focusable';
import { InputContext, FieldErrors, Field } from './field';
import { FormContext, disableAutocomplete } from './form';
import type { FieldProps } from './field';
import type { JSX } from 'solid-js';
import type { DOMElement } from 'solid-js/jsx-runtime';

type InputProps = Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'aria-required' | 'prefix' | 'suffix'> & {
	multiline?: never;
};
type TextareaProps = Omit<JSX.TextareaHTMLAttributes<HTMLTextAreaElement>, 'aria-required' | 'prefix' | 'suffix'> & {
	multiline: true;
};

type Props = (InputProps | TextareaProps) & {
	prefixElement?: JSX.Element;
	suffixElement?: JSX.Element;
};

type ClickEvent = MouseEvent & {
	currentTarget: HTMLInputElement;
	target: DOMElement;
};

export function Input(inputProps: Props) {
	const { data } = useContext(FormContext);
	const [context] = useContext(InputContext);
	const focusable = useFocusable<Props>();
	const merged = mergeProps(context, focusable, inputProps);
	const [custom, props] = splitProps(merged, ['multiline', 'prefixElement', 'suffixElement']);
	const [ref, setRef] = createSignal<HTMLInputElement | HTMLTextAreaElement>();

	// `autofocus` only triggers by default on initial page. to make it work in client-side rendered applications
	// we need to manually trigger the focus after the element exists on the page
	createEffect(() => {
		const el = ref();
		if (props.autofocus && el) {
			setTimeout(() => el?.focus());
		}
	});

	return (
		<div class="relative w-full">
			<div class="pointer-events-none absolute inset-y-2 left-2 flex flex-row items-center">{custom.prefixElement}</div>
			{/* @ts-expect-error textarea and input don't really overlap, but this should be safe based on the input props */}
			<Dynamic
				component={custom.multiline ? 'textarea' : 'input'}
				{...props}
				autocapitalize={props.autocapitalize ?? (props.inputMode === 'email' ? 'none' : undefined)}
				readonly={data.pending || props.readonly}
				aria-readonly={data.pending || props.readonly}
				aria-required={props.required}
				onClick={(event: ClickEvent) => {
					const onClick = props.onClick as (e: ClickEvent) => void;
					if (onClick) {
						onClick(event);
					}
					if (event.defaultPrevented) {
						return;
					}
					if (props.readonly) {
						(event.currentTarget as HTMLInputElement).select();
					}
				}}
				onFocus={props.onFocus}
				{...disableAutocomplete(props)}
				class={twMerge(
					twJoin(
						'size-full grow-0 appearance-none rounded-lg border border-solid border-neutral bg-white px-4 py-3 text-neutral-900 transition-all duration-200 outline-none focus-visible:ring-1 focus-visible:ring-brand-700 aria-invalid:border-red-600 aria-readonly:bg-neutral-100 aria-readonly:text-neutral-700',
						'type' in props &&
							(props.type === 'date' || props.type === 'month') &&
							'size-full min-w-full pe-0 text-start text-nowrap whitespace-nowrap calendar-picker-indicator:absolute calendar-picker-indicator:inset-0 calendar-picker-indicator:z-0 calendar-picker-indicator:size-full calendar-picker-indicator:opacity-0 date-and-time-value:block date-and-time-value:w-full date-and-time-value:ps-0 date-and-time-value:text-start',
					),
					props.class,
				)}
				ref={(el: HTMLInputElement | HTMLTextAreaElement) => {
					if (typeof props.ref === 'function') {
						// @ts-expect-error ref complaint between input vs textarea
						props.ref(el);
					}
					setRef(el);
				}}
			/>
			<div class="pointer-events-none absolute inset-y-2 right-2 flex flex-row items-center">
				{custom.suffixElement}
			</div>
		</div>
	);
}

export function TextField(props: FieldProps) {
	return (
		<Field {...props} class={twMerge('flex flex-col gap-1', props.class)}>
			{props.children}
			<FieldErrors />
		</Field>
	);
}
